import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
export const REDIRECT_AFTER_LOGIN_KEY = "redirect_after_login";
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];

/**
 * Déconnecte automatiquement l'utilisateur après 10 minutes sans aucune
 * interaction (souris, clavier, tactile, scroll), et mémorise la page sur
 * laquelle il se trouvait pour l'y ramener après sa prochaine connexion
 * (voir Login.js, qui lit REDIRECT_AFTER_LOGIN_KEY).
 * Ne s'active que pour un utilisateur connecté ; ne fait rien sinon.
 */
export default function InactivityWatcher() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const timerRef = useRef(null);
  const locationRef = useRef(location);

  useEffect(() => { locationRef.current = location; }, [location]);

  useEffect(() => {
    if (!user) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    const doLogout = () => {
      const path = `${locationRef.current.pathname}${locationRef.current.search}`;
      if (path && path.startsWith("/app")) {
        localStorage.setItem(REDIRECT_AFTER_LOGIN_KEY, path);
      }
      logout();
      toast.info("Vous avez été déconnecté après 10 minutes d'inactivité.");
      navigate("/login", { replace: true });
    };

    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(doLogout, TIMEOUT_MS);
    };

    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, resetTimer));
    resetTimer();

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, resetTimer));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, logout, navigate]);

  return null;
}
